import { normalizeHiddenQuestions } from '../domain/evaluation';
/**
 * Firestore documents in, domain objects out.
 *
 * Every read in the app passes through here exactly once. The point is that no
 * component ever holds a `Timestamp`, an absent field, or a value of the wrong
 * type — including when an old document written by a previous version of the
 * site is the one being read. Defaults are applied on the way in rather than
 * defended against at every use site.
 */

import { Timestamp, type DocumentSnapshot, type DocumentData } from 'firebase/firestore';

import type {
  AgendaEvent,
  Announcement,
  AppUser,
  Course,
  CourseDocument,
  DeliveryRecord,
  DocumentCategory,
  Enrollment,
  InboxItem,
  NotificationEvent,
  NotificationGroup,
  PublicationState,
  PushSubscriptionRecord,
  SeenRecord,
  Semester,
  StudentActivityEntry,
  Todo,
  TodoAttachment,
} from '../domain/types';
import { coercePrefs } from '../design/theme';
import { ADMIN_EMAIL } from '../../shared/authz';

// ── Primitives ───────────────────────────────────────────────────────────────

/**
 * Any of the shapes a date has ever been stored in, as a Date.
 *
 * Old documents hold ISO strings; current ones hold Timestamps; an optimistic
 * local write briefly holds a real Date before the server round-trips. All
 * three are legitimate, so all three are accepted.
 */
export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // A pending serverTimestamp() reads back as null until the server answers.
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

export function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function strArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Publication state, defaulting to draft.
 *
 * Draft is the safe default on purpose: a malformed or half-migrated document
 * should disappear from the student view, never leak into it.
 */
export function pubState(value: unknown): PublicationState {
  return value === 'published' ? 'published' : 'draft';
}

// ── Documents ────────────────────────────────────────────────────────────────

export function toUser(snap: DocumentSnapshot<DocumentData>): AppUser {
  const d = snap.data() ?? {};
  const email = str(d.email).toLowerCase();
  return {
    uid: snap.id,
    email,
    displayName: str(d.displayName) || email.split('@')[0] || 'Unknown',
    photoURL: str(d.photoURL) || null,
    // Role is derived, not stored. One admin address, decided in one place.
    role: email === ADMIN_EMAIL ? 'admin' : 'student',
    prefs: coercePrefs(d.prefs),
    lastLoginAt: toDate(d.lastLoginAt) ?? toDate(d.seenAt) ?? toDate(d.lastSeen),
    createdAt: toDate(d.createdAt),
  };
}

export function toSemester(snap: DocumentSnapshot<DocumentData>): Semester {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    term: str(d.term, 'Untitled semester'),
    announcementsDisabled: d.announcementsDisabled === true,
    institution: str(d.institution),
    // A semester with no dates would be visible to nobody, which is the
    // correct failure: the epoch window is closed, not open.
    startAt: toDate(d.startAt) ?? new Date(0),
    endAt: toDate(d.endAt) ?? new Date(0),
    timezone: str(d.timezone, 'America/Chicago'),
    accent: str(d.accent, '#1f6f4a'),
    description: str(d.description),
    reopenedUntil: toDate(d.reopenedUntil),
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
    duplicatedFrom: str(d.duplicatedFrom) || null,
  };
}

export function toEnrollment(snap: DocumentSnapshot<DocumentData>): Enrollment {
  const d = snap.data() ?? {};
  return {
    uid: snap.id,
    email: str(d.email).toLowerCase(),
    displayName: str(d.displayName),
    enrolledAt: toDate(d.enrolledAt),
    enrolledByUid: str(d.enrolledByUid),
  };
}

export function toCourse(semesterId: string) {
  return (snap: DocumentSnapshot<DocumentData>): Course => {
    const d = snap.data() ?? {};
    return {
      id: snap.id,
      semesterId,
      title: str(d.title, 'Untitled course'),
      announcementsDisabled: d.announcementsDisabled === true,
      hiddenEvaluationQuestions: normalizeHiddenQuestions(d.hiddenEvaluationQuestions),
      termSegmentOverride: ['full', 'first', 'second'].includes(d.termSegmentOverride) ? d.termSegmentOverride as Course['termSegmentOverride'] : null,
      meetingDays: Array.isArray(d.meetingDays) ? d.meetingDays.filter((day: unknown): day is string => typeof day === 'string') : [],
      meetingTime: str(d.meetingTime),
      code: str(d.code),
      instructor: str(d.instructor),
      description: str(d.description),
      meets: str(d.meets),
      classroom: str(d.classroom),
      // Anything unrecognised — including an older record with no value —
      // becomes '' rather than a guess.
      meetingType: (['traditional', 'flex', 'online'] as const).includes(
        d.meetingType as 'traditional' | 'flex' | 'online'
      )
        ? (d.meetingType as Course['meetingType'])
        : '',
      startAt: toDate(d.startAt),
      endAt: toDate(d.endAt),
      accent: str(d.accent) || '',
      order: num(d.order, 0),
      state: pubState(d.state),
      notificationGroupId: str(d.notificationGroupId) || null,
      createdAt: toDate(d.createdAt),
      updatedAt: toDate(d.updatedAt),
    };
  };
}

export function toAnnouncement(semesterId: string) {
  return (snap: DocumentSnapshot<DocumentData>): Announcement => {
    const d = snap.data() ?? {};
    return {
      id: snap.id,
      semesterId,
      courseId: str(d.courseId) || null,
      title: str(d.title, 'Untitled'),
      bodyHtml: str(d.bodyHtml),
      state: pubState(d.state),
      publishAt: toDate(d.publishAt),
      notifiedAt: toDate(d.notifiedAt),
      authorUid: str(d.authorUid),
      createdAt: toDate(d.createdAt),
      updatedAt: toDate(d.updatedAt),
    };
  };
}

function toAttachment(raw: unknown): TodoAttachment | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  const storagePath = str(a.storagePath);
  if (!storagePath) return null;
  return {
    id: str(a.id) || storagePath,
    name: str(a.name) || storagePath.split('/').pop() || 'Attachment',
    storagePath,
    contentType: str(a.contentType, 'application/octet-stream'),
    size: num(a.size, 0),
    uploadedAt: toDate(a.uploadedAt),
  };
}

export function toTodo(semesterId: string) {
  return (snap: DocumentSnapshot<DocumentData>): Todo => {
    const d = snap.data() ?? {};
    return {
      id: snap.id,
      semesterId,
      courseId: str(d.courseId) || null,
      title: str(d.title, 'Untitled'),
      notesHtml: str(d.notesHtml),
      instructionPdfs: (Array.isArray(d.instructionPdfs) ? d.instructionPdfs : []).map(toAttachment).filter((a): a is TodoAttachment => a !== null),
      assignedAt: toDate(d.assignedAt) ?? toDate(d.createdAt),
      dueAt: toDate(d.dueAt),
      completed: bool(d.completed),
      completedAt: toDate(d.completedAt),
      submissionHtml: str(d.submissionHtml),
      submissionComments: (Array.isArray(d.submissionComments) ? d.submissionComments : [])
        .filter((c) => c && typeof c.id === 'string' && typeof c.body === 'string')
        .map((c) => ({ id: str(c.id), professor: str(c.professor), body: str(c.body), createdAt: toDate(c.createdAt) })),
      attachments: (Array.isArray(d.attachments) ? d.attachments : [])
        .map(toAttachment)
        .filter((a): a is TodoAttachment => a !== null),
      grade: str(d.grade),
      gradedAt: toDate(d.gradedAt),
      state: pubState(d.state),
      completionNotifiedAt: toDate(d.completionNotifiedAt),
      order: num(d.order, 0),
      createdAt: toDate(d.createdAt),
      updatedAt: toDate(d.updatedAt),
    };
  };
}

export function toDocumentCategory(semesterId: string) {
  return (snap: DocumentSnapshot<DocumentData>): DocumentCategory => {
    const d = snap.data() ?? {};
    return {
      id: snap.id,
      semesterId,
      name: str(d.name, 'Uncategorised'),
      order: num(d.order, 0),
    };
  };
}

export function toCourseDocument(semesterId: string) {
  return (snap: DocumentSnapshot<DocumentData>): CourseDocument => {
    const d = snap.data() ?? {};
    return {
      id: snap.id,
      semesterId,
      courseId: str(d.courseId) || null,
      categoryId: str(d.categoryId) || null,
      kind: d.kind === 'page' ? 'page' : 'file',
      bodyHtml: str(d.bodyHtml),
      evaluationAnswers: Object.fromEntries(Object.entries(d.evaluationAnswers && typeof d.evaluationAnswers === 'object' ? d.evaluationAnswers : {}).filter(([key, value]) => /^(?:[1-9]|10)$/.test(key) && typeof value === 'string')) as Record<string, string>,
      name: str(d.name, 'Untitled file'),
      hiddenEvaluationQuestions: normalizeHiddenQuestions(d.hiddenEvaluationQuestions),
      description: str(d.description),
      storagePath: str(d.storagePath),
      contentType: str(d.contentType, 'application/octet-stream'),
      size: num(d.size, 0),
      state: pubState(d.state),
      publishedAt: toDate(d.publishedAt),
      notifiedAt: toDate(d.notifiedAt),
      uploadedByUid: str(d.uploadedByUid),
      createdAt: toDate(d.createdAt),
      updatedAt: toDate(d.updatedAt),
    };
  };
}

export function toAgendaEvent(semesterId: string) {
  return (snap: DocumentSnapshot<DocumentData>): AgendaEvent => {
    const d = snap.data() ?? {};
    return {
      id: snap.id,
      semesterId,
      courseId: str(d.courseId) || null,
      title: str(d.title, 'Untitled event'),
      description: str(d.description),
      startAt: toDate(d.startAt) ?? new Date(0),
      endAt: toDate(d.endAt),
      allDay: bool(d.allDay),
      state: pubState(d.state),
      attachments: (Array.isArray(d.attachments) ? d.attachments : [])
        .map(toAttachment)
        .filter((a): a is TodoAttachment => a !== null),
      location: str(d.location) || null,
      url: str(d.url) || null,
      recurrence: str(d.recurrence) || null,
      createdAt: toDate(d.createdAt),
      updatedAt: toDate(d.updatedAt),
    };
  };
}

export function toNotificationGroup(snap: DocumentSnapshot<DocumentData>): NotificationGroup {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    name: str(d.name, 'Untitled group'),
    semesterId: str(d.semesterId) || null,
    memberUids: strArray(d.memberUids),
    isDefault: d.isDefault === true,
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

export function toInboxItem(snap: DocumentSnapshot<DocumentData>): InboxItem {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    type: (str(d.type, 'announcement') as InboxItem['type']),
    eventId: str(d.eventId),
    semesterId: str(d.semesterId) || null,
    title: str(d.title),
    summary: str(d.summary),
    // Never "/". A notification that lands on the home screen has failed.
    deepLink: str(d.deepLink) || '/',
    createdAt: toDate(d.createdAt),
    readAt: toDate(d.readAt),
  };
}

export function toDelivery(snap: DocumentSnapshot<DocumentData>): DeliveryRecord {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    eventId: str(d.eventId),
    eventType: str(d.eventType, 'announcement') as DeliveryRecord['eventType'],
    channel: str(d.channel, 'email') as DeliveryRecord['channel'],
    recipientUid: str(d.recipientUid),
    recipientEmail: str(d.recipientEmail),
    recipientName: str(d.recipientName),
    semesterId: str(d.semesterId) || null,
    deepLink: str(d.deepLink),
    subject: str(d.subject),
    status: str(d.status, 'pending') as DeliveryRecord['status'],
    attempts: num(d.attempts, 0),
    lastAttemptAt: toDate(d.lastAttemptAt),
    nextAttemptAt: toDate(d.nextAttemptAt),
    lastError: str(d.lastError) || null,
    providerMessageId: str(d.providerMessageId) || null,
    deliveredAt: toDate(d.deliveredAt),
    providerEvent: str(d.providerEvent),
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

export function toNotificationEvent(snap: DocumentSnapshot<DocumentData>): NotificationEvent {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    type: str(d.type, 'announcement') as NotificationEvent['type'],
    semesterId: str(d.semesterId) || null,
    subjectPath: str(d.subjectPath),
    title: str(d.title),
    summary: str(d.summary),
    deepLink: str(d.deepLink),
    idempotencyKey: str(d.idempotencyKey),
    recipientCount: num(d.recipientCount, 0),
    createdAt: toDate(d.createdAt),
  };
}

export function toPushSubscription(snap: DocumentSnapshot<DocumentData>): PushSubscriptionRecord {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    uid: str(d.uid),
    endpoint: str(d.endpoint),
    p256dh: str(d.p256dh),
    auth: str(d.auth),
    device: str(d.device, 'Unknown device'),
    createdAt: toDate(d.createdAt),
    lastSeenAt: toDate(d.lastSeenAt),
    failureCount: num(d.failureCount, 0),
  };
}

export function toSeenRecord(snap: DocumentSnapshot<DocumentData>): SeenRecord {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    uid: str(d.uid),
    contentType: str(d.contentType, 'announcement') as SeenRecord['contentType'],
    contentId: str(d.contentId),
    firstSeenAt: toDate(d.firstSeenAt),
    lastSeenAt: toDate(d.lastSeenAt),
    viewCount: num(d.viewCount, 0),
  };
}

export function toStudentActivity(snap: DocumentSnapshot<DocumentData>): StudentActivityEntry {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    uid: str(d.uid),
    email: str(d.email),
    kind: str(d.kind, 'view') as StudentActivityEntry['kind'],
    semesterId: str(d.semesterId) || null,
    contentType: (str(d.contentType) || null) as StudentActivityEntry['contentType'],
    contentId: str(d.contentId) || null,
    label: str(d.label),
    at: toDate(d.at),
  };
}
